/** Fund-register surface (demo-v2 W1) — /api/register.
 *
 * The namespace is DARK behind the backend's fund_register_enabled flag:
 * every route 404s while it is off, so a probe on it IS the flag read
 * (same idiom as rkdecks/api.ts). While the register is on, the Funds
 * page shows the "register synced · HH:MM" stamp + manual refresh, and
 * the mark-as-fund pill is hidden everywhere — the register is the only
 * door for founding funds.
 */

import { useEffect, useState } from 'react'
import { ApiError, apiFetch } from '../../lib/api'

export interface RegisterEntry {
  agreement_id: string
  fund_name: string
  aliases: string[]
  jurisdiction: string
  legal_form: string | null
  agreement_date: string | null
  status: 'active' | 'terminated'
  /** Set once the fund is materialized in the product. */
  fund_id: string | null
}

export interface RegisterSyncInfo {
  source_file: string
  synced_at: string
  trigger: 'watcher' | 'manual'
  upserted: number
  unchanged: number
  /** [row_number, reason] — malformed register rows, loud, never dropped. */
  skipped: [number, string][]
  new_agreement_ids: string[]
}

export interface RegisterResponse {
  entries: RegisterEntry[]
  last_sync: RegisterSyncInfo | null
}

export interface RegisterSyncResponse {
  ran: boolean
  report: RegisterSyncInfo | null
}

export function getRegister(): Promise<RegisterResponse> {
  return apiFetch<RegisterResponse>('/api/register')
}

export function syncRegisterNow(): Promise<RegisterSyncResponse> {
  return apiFetch<RegisterSyncResponse>('/api/register/sync', { method: 'POST' })
}

/* ── flag probe ──────────────────────────────────────────────────────── */

let probe: Promise<boolean> | null = null

/** True once GET /api/register answers 200; false on the dark-namespace 404. */
export function probeRegister(): Promise<boolean> {
  if (!probe) {
    probe = apiFetch<RegisterResponse>('/api/register')
      .then(() => true)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) return false
        // Auth/network hiccups must not latch the flag off for the session.
        probe = null
        return false
      })
  }
  return probe
}

/** null while probing, then the flag. */
export function useRegisterEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    probeRegister().then((on) => {
      if (!cancelled) setEnabled(on)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return enabled
}
